import { apiClient } from '@core/network/apiClient';
import { logger } from '@core/utils/logger';
import { tokenStorage } from '@features/auth/utils/tokenStorage';
import type { CurrentUser } from '@core/types/models';
import type {
  AuthSession,
  LoginCredentials,
  LoginResponse,
} from '@features/auth/types/auth.types';

/**
 * Auth API. Backed by MSW mocks in dev/test; the same contract targets the real
 * backend in production. Login returns only a token + role; the full identity
 * comes from GET /me (contract has no user object on the login response).
 */
export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const { data } = await apiClient.post<LoginResponse>('/auth/login', credentials);
    // Store the token first so the immediate /me call is authenticated.
    tokenStorage.setAccessToken(data.access_token);
    try {
      const user = await authService.getSession();
      return { user, token: data.access_token };
    } catch (err) {
      tokenStorage.clear();
      throw err;
    }
  },

  /** Resolve the current user from the stored token (used on app boot / refresh). */
  async getSession(): Promise<CurrentUser> {
    const { data } = await apiClient.get<CurrentUser>('/me');
    return data;
  },

  /**
   * Sign out. Dropping the token client-side leaves it valid until it expires,
   * so the server is told to revoke it too. A failed revoke is logged and
   * swallowed: never rejecting is what lets the caller clear the session
   * unconditionally, and being stuck signed in is worse than a stale token.
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/me/tokens/revoke');
    } catch {
      logger.warn('Token revoke failed on sign-out');
    }
  },
};
